import { useEffect } from "react";

// -----------------------------------------------------------------------------
// useCopyProtect — enables mild copy/reverse-engineering deterrents:
//   • Blocks right-click context menu (Ctrl+click on Mac still works — power
//     users can always use DevTools; that's fine, this is friction not a lock).
//   • Suppresses text selection except in <input>/<textarea>/[contenteditable].
//   • Watermarks the document with hidden HTML comments referencing our
//     Trademark & Patent claims so if the page is scraped, our IP claim is
//     literally in the copy.
//   • Warns when a common screenshotting hotkey is pressed (Chrome/Firefox
//     don't expose real screenshot events; this is the best we can do).
//
// Genuine protection lives in the legal notice + /legal page + IP filings.
// This hook is *deterrent*, not enforcement.
// -----------------------------------------------------------------------------
export function useCopyProtect({ enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return;

    const blockContext = (e) => {
      // Allow right-click inside form fields so users can still paste/edit.
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.preventDefault();
    };

    const blockCopy = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      // Modify the payload to include our watermark instead of blocking outright.
      try {
        const selection = window.getSelection()?.toString() || "";
        e.preventDefault();
        e.clipboardData?.setData(
          "text/plain",
          `${selection}\n\n— © find2near · content protected under ` +
            `Copyright & Trademark. Reproduction requires written permission.`
        );
      } catch { /* clipboard blocked — ignore */ }
    };

    const warnHotkey = (e) => {
      // Windows PrintScreen doesn't fire keydown reliably; on Mac Cmd-Shift-3/4
      // never fires at all. We can only best-effort catch the DevTools open.
      if (e.key === "F12") {
        e.preventDefault();
        console.warn("[find2near] Content on this page is protected. See /legal.");
      }
    };

    document.addEventListener("contextmenu", blockContext);
    document.addEventListener("copy", blockCopy);
    document.addEventListener("keydown", warnHotkey);

    // Embed hidden watermark comment nodes into the DOM once.
    const marker = document.createComment(
      " find2near — © 2026, All Rights Reserved. Trademark & Patent Pending. " +
      "Unauthorised copying, framing or reverse-engineering is a legal violation. " +
      "See /legal for the full notice. "
    );
    document.body?.appendChild(marker);

    return () => {
      document.removeEventListener("contextmenu", blockContext);
      document.removeEventListener("copy", blockCopy);
      document.removeEventListener("keydown", warnHotkey);
      marker?.remove?.();
    };
  }, [enabled]);
}

export default useCopyProtect;
